import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { normalizeSessionCooling } from "../utils/sessionCoolingHelpers";
import { checkAppAuth, loginAppUser, logoutAppUser } from "./appAuthSlice";

const initialState = {
  active: false,
  exempt: false,
  approaching: false,
  coolingUntil: null,
  coolingRemainingMs: 0,
  sessionRemainingMs: null,
  consecutiveLimitMs: null,
  coolingDurationMs: null,
  visible: false,
  warningVisible: false,
  warningDismissedRemainingMs: null,
  heartbeatStatus: "idle",
};

function applyCoolingState(state, payload) {
  const next = normalizeSessionCooling(payload);
  const prevRemaining = state.sessionRemainingMs;

  if (
    next.sessionRemainingMs != null &&
    prevRemaining != null &&
    next.sessionRemainingMs > prevRemaining + 60 * 1000
  ) {
    state.warningDismissedRemainingMs = null;
  }
  if (next.active) {
    state.warningDismissedRemainingMs = null;
  }

  state.active = next.active;
  state.exempt = next.exempt;
  state.approaching = next.approaching;
  state.coolingUntil = next.coolingUntil;
  state.coolingRemainingMs = next.coolingRemainingMs;
  state.sessionRemainingMs = next.sessionRemainingMs;
  state.consecutiveLimitMs = next.consecutiveLimitMs;
  state.coolingDurationMs = next.coolingDurationMs;
  state.visible = next.active;
  state.warningVisible =
    next.approaching &&
    !next.active &&
    !next.exempt &&
    (state.warningDismissedRemainingMs == null ||
      (next.sessionRemainingMs != null &&
        next.sessionRemainingMs < state.warningDismissedRemainingMs));
}

export const pingSessionHeartbeat = createAsyncThunk(
  "sessionCooling/ping",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem(APP_TOKEN_KEY);
    if (!token) return rejectWithValue({ silent: true });
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/session/heartbeat`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.sessionCooling;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Heartbeat failed" });
    }
  }
);

const sessionCoolingSlice = createSlice({
  name: "sessionCooling",
  initialState,
  reducers: {
    syncSessionCooling: (state, action) => {
      applyCoolingState(state, action.payload);
    },
    tickCoolingRemaining: (state) => {
      if (!state.active || state.coolingRemainingMs <= 0) return;
      state.coolingRemainingMs = Math.max(0, state.coolingRemainingMs - 1000);
      if (state.coolingRemainingMs <= 0) {
        state.active = false;
        state.visible = false;
        state.coolingUntil = null;
      }
    },
    tickSessionRemaining: (state) => {
      if (!state.warningVisible || state.sessionRemainingMs == null || state.sessionRemainingMs <= 0) {
        return;
      }
      state.sessionRemainingMs = Math.max(0, state.sessionRemainingMs - 1000);
    },
    clearSessionCooling: () => initialState,
    dismissCoolingWarning: (state) => {
      if (state.sessionRemainingMs != null) {
        state.warningDismissedRemainingMs = state.sessionRemainingMs;
      }
      state.warningVisible = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(pingSessionHeartbeat.fulfilled, (state, action) => {
        state.heartbeatStatus = "idle";
        applyCoolingState(state, action.payload);
      })
      .addCase(checkAppAuth.fulfilled, (state, action) => {
        if (!action.payload?.sessionCooling) return;
        applyCoolingState(state, action.payload.sessionCooling);
      })
      .addCase(loginAppUser.fulfilled, (state, action) => {
        if (!action.payload?.sessionCooling) return;
        applyCoolingState(state, action.payload.sessionCooling);
      })
      .addCase(logoutAppUser.fulfilled, () => initialState);
  },
});

export const { syncSessionCooling, tickCoolingRemaining, tickSessionRemaining, clearSessionCooling, dismissCoolingWarning } =
  sessionCoolingSlice.actions;

export default sessionCoolingSlice.reducer;
